import React, { useCallback, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Select } from "components/forms";
import { useUserActions } from "store";
import { ISendQuestionData } from "store/types";
import { toastify } from "utils/tyostify";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const boxSx: BoxProps["sx"] = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "474px",
  bgcolor: "white",
  borderRadius: "10px"
};

const categories: string[] = [
  "Can't do something I used to do",
  "Wish I could do something",
  "Need help doing something"
];

export const SendQuestionModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { sendEmailWithQuestionAction } = useUserActions();

  const [inputValue, setInputValue] = useState<string>("");
  const [sendLoader, setSendLoader] = useState<boolean>(false);
  const [category, setCategory] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  const sendQuestion = async () => {
    try {
      if (!inputValue) return;

      const payload: ISendQuestionData = {
        message: inputValue,
        page: window.location.href,
        type: "question",
        category,
        ...(files.length && { files })
      };
      setSendLoader(true);
      await sendEmailWithQuestionAction(payload);
      onClose();
      setFiles([]);
      setInputValue("");
      setCategory("");
      toastify({ message: "We received your question and we will get back to you shortly.", type: "success" });
    } catch (_error) {
      toastify({ message: "Something went wrong", type: "error" });
    } finally {
      setSendLoader(false);
    }
  };

  const onAddFile = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      if (!e.target.files) return;

      const filesList = Object.values(e.target.files);

      setFiles((prevState) => [
        ...prevState,
        ...filesList.filter((newFile) => !prevState.some((oldFile) => newFile.name === oldFile.name))
      ]);
    },
    [setFiles]
  );

  const onDeleteFile = useCallback(
    (file: File) => {
      setFiles((prevState) =>
        prevState.filter((item) => !(item.lastModified === file.lastModified && item.name && file.name))
      );
    },
    [setFiles]
  );

  const onAttachCLick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (inputRef.current) {
        inputRef.current.click();
      }
    },
    [inputRef]
  );

  return (
    <Modal onClose={onClose} sx={{ ".MuiBackdrop-root": { bgcolor: "transparent" } }} open={isOpen}>
      {/* id needed to open a modal window using userPilot */}
      <Box id="SendQuestionModal" sx={boxSx}>
        <Stack
          gap="15px"
          sx={{
            borderRadius: "8px",
            backgroundColor: "#fff",
            padding: "30px",
            boxShadow: "rgba(0, 0, 0, 0.25) -2px 2px 15px -2px"
          }}
        >
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ marginBottom: "10px" }}>
                <Typography fontSize={16} lineHeight={1.5} fontWeight="500" sx={{ marginBottom: "5px" }}>
                  How can we help?
                </Typography>
                <Typography fontSize={14} lineHeight={1.5} color="#5d7782">
                  We will get back to you shortly
                </Typography>
              </Box>
              <IconButton size="sm" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ height: "1px", borderColor: "#DBE8F5" }} />
          </Box>
          <Stack sx={{ gap: "16px" }}>
            <Select
              formProps={{ sx: { ".MuiOutlinedInput-root": { borderRadius: "8px" } } }}
              variant="outlined"
              label="Categories"
              value={category}
              onChange={(e) => setCategory(e.target.value as string)}
              items={categories}
            />
            <TextField
              id="outlined-multiline-static"
              label="Message"
              multiline
              rows={6}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <Stack sx={{ alignItems: "flex-start", gap: "7px", overflow: "hidden" }}>
              {files.map((file) => (
                <Stack
                  key={file.lastModified}
                  direction="row"
                  sx={{ gap: "5px", overflow: "hidden", maxWidth: "100%" }}
                >
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontWeight: 400,
                      color: "#496682",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {file.name}
                  </Typography>
                  <IconButton size="sm" onClick={() => onDeleteFile(file)}>
                    <CloseIcon sx={{ bgcolor: "#071F35", borderRadius: "50%", color: "white" }} />
                  </IconButton>
                </Stack>
              ))}
              <Stack
                onClick={onAttachCLick}
                sx={{ alignItems: "center", gap: "4px", cursor: "pointer" }}
                direction="row"
              >
                <AddIcon fontSize="sm" sx={{ color: "#26A6AE" }} />
                <Typography sx={{ color: "#26A6AE", fontSize: "15px" }}>Attach file</Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" gap="15px" alignItems="center" justifyContent="flex-end">
            <Button sx={{ cursor: "pointer" }} variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            {sendLoader ? (
              <Box
                sx={{ display: "flex", width: "115px", height: "30px", justifyContent: "center", alignItems: "center" }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Button
                disabled={!inputValue || !category}
                sx={{ cursor: "pointer" }}
                onClick={sendQuestion}
                variant="contained"
              >
                Send
              </Button>
            )}
          </Stack>
        </Stack>
        <input onChange={onAddFile} multiple ref={inputRef} hidden type="file" />
      </Box>
    </Modal>
  );
};
